/**
 * Harmonia Product Mosaic (HS-MOSAIC-001)
 * Apple Photos 風商品グリッドショーケース
 *
 * Features:
 * - IntersectionObserver でスクロール時に登場アニメーション
 * - スキャッタードパターンのランダム回転
 * - ランダムアニメーションモードの遅延設定
 * - Shopify テーマエディタ対応
 */

(function() {
  'use strict';

  // ============================================================
  // Configuration
  // ============================================================

  const SCATTER_ROTATION_RANGE = 3; // -3° to +3°
  const DEFAULT_STAGGER_DELAY = 80; // ms
  const RANDOM_DELAY_MAX = 500; // ms

  // ============================================================
  // MosaicTouchHandler: Tap Toggle for Hybrid Mode
  // ============================================================

  class MosaicTouchHandler {
    constructor(container) {
      this.container = container;
      this.hybridItems = [];
      this.activeItem = null;
      this.isTouchDevice = this.detectTouchDevice();
      this.displayMode = container.dataset.displayMode;

      // Only initialize for hybrid mode on touch devices
      if (this.displayMode === 'hybrid' && this.isTouchDevice) {
        this.init();
      }
    }

    detectTouchDevice() {
      // Check for touch capability and no fine pointer (mouse)
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(hover: none)').matches
      );
    }

    init() {
      // Add tap-toggle modifier class
      this.container.classList.add('harmonia-mosaic--tap-toggle');

      // Get all hybrid items
      this.hybridItems = [...this.container.querySelectorAll('[data-mosaic-hybrid]')];

      // Bind event handlers
      this.handleTap = this.handleTap.bind(this);
      this.handleOutsideTap = this.handleOutsideTap.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);

      // Attach listeners
      this.hybridItems.forEach(item => {
        item.addEventListener('click', this.handleTap, { passive: false });
      });

      // Listen for taps outside the mosaic to close active overlay
      document.addEventListener('click', this.handleOutsideTap, { passive: true });

      // Close overlay when scrolling/swiping
      document.addEventListener('touchmove', this.handleTouchMove, { passive: true });

      console.log('[MosaicTouchHandler] Initialized for hybrid mode', {
        items: this.hybridItems.length
      });
    }

    handleTap(e) {
      const item = e.currentTarget;

      if (!item.classList.contains('is-overlay-active')) {
        // First tap: Show overlay, prevent navigation
        e.preventDefault();
        this.showOverlay(item);
      }
      // Second tap: Allow default (navigate to product)
    }

    showOverlay(item) {
      // Close any previously active item
      if (this.activeItem && this.activeItem !== item) {
        this.hideOverlay(this.activeItem);
      }

      // Activate this item
      item.classList.add('is-overlay-active');
      item.setAttribute('aria-expanded', 'true');
      this.activeItem = item;

      console.log('[MosaicTouchHandler] Overlay shown');
    }

    hideOverlay(item) {
      if (!item) return;

      item.classList.remove('is-overlay-active');
      item.setAttribute('aria-expanded', 'false');

      if (this.activeItem === item) {
        this.activeItem = null;
      }

      console.log('[MosaicTouchHandler] Overlay hidden');
    }

    handleOutsideTap(e) {
      if (!this.activeItem) return;

      // Check if tap was outside the active item
      if (!this.activeItem.contains(e.target)) {
        this.hideOverlay(this.activeItem);
      }
    }

    handleTouchMove() {
      // Close overlay when scrolling/swiping
      if (this.activeItem) {
        this.hideOverlay(this.activeItem);
      }
    }

    hideAll() {
      this.hybridItems.forEach(item => {
        this.hideOverlay(item);
      });
    }

    destroy() {
      this.hybridItems.forEach(item => {
        item.removeEventListener('click', this.handleTap);
      });

      document.removeEventListener('click', this.handleOutsideTap);
      document.removeEventListener('touchmove', this.handleTouchMove);

      this.hideAll();
      this.container.classList.remove('harmonia-mosaic--tap-toggle');

      console.log('[MosaicTouchHandler] Destroyed');
    }
  }

  // ============================================================
  // HarmoniaMosaicCarousel: Infinite Marquee Controller
  // ============================================================

  class HarmoniaMosaicCarousel {
    constructor(container) {
      this.container = container;
      this.track = container.querySelector('.harmonia-mosaic__carousel-track');
      this.viewport = container.querySelector('.harmonia-mosaic__carousel-viewport');

      // Settings from data attributes
      this.speed = this.track?.dataset.carouselSpeed || 'medium';
      this.direction = this.track?.dataset.carouselDirection || 'left';

      this.resizeObserver = null;

      this.init();
    }

    init() {
      if (!this.track) return;

      // Calculate and set animation duration
      this.calculateDuration();

      // Recalculate on resize (responsive width changes affect duration)
      this.resizeObserver = new ResizeObserver(() => this.calculateDuration());
      this.resizeObserver.observe(this.track);

      console.log('[HarmoniaMosaicCarousel] Initialized', {
        speed: this.speed,
        direction: this.direction
      });
    }

    calculateDuration() {
      // Measure one set's width to derive the correct duration
      const set = this.track.querySelector('.harmonia-mosaic__carousel-set');
      if (!set) return;

      const setWidth = set.scrollWidth;

      // Speed mapping: pixels per second
      const speedMap = { slow: 30, medium: 50, fast: 80 };
      const pxPerSec = speedMap[this.speed] || 50;

      const duration = setWidth / pxPerSec;
      this.track.style.setProperty('--carousel-duration', `${duration}s`);
    }

    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      console.log('[HarmoniaMosaicCarousel] Destroyed');
    }
  }

  // ============================================================
  // HarmoniaMosaic Class
  // ============================================================

  class HarmoniaMosaic {
    constructor(container) {
      this.container = container;
      this.grid = container.querySelector('.harmonia-mosaic__grid');
      this.items = [...container.querySelectorAll('.harmonia-mosaic__item')];

      // Settings from data attributes
      this.pattern = container.dataset.pattern || 'equal-3x3';
      this.animation = container.dataset.animation || 'stagger-scale';
      this.kenburns = container.dataset.kenburns === 'true';
      this.displayMode = container.dataset.displayMode || 'product';

      // State
      this.isAnimated = false;

      // Touch handler for hybrid mode
      this.touchHandler = null;

      // Carousel handler (for carousel pattern)
      this.carouselHandler = null;

      // Accessibility
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      // Initialize
      this.init();
    }

    init() {
      // Carousel mode: delegate to HarmoniaMosaicCarousel
      if (this.pattern === 'carousel' || this.pattern === 'magazine-carousel') {
        this.carouselHandler = new HarmoniaMosaicCarousel(this.container);

        // Initialize touch handler for hybrid display within carousel
        this.touchHandler = new MosaicTouchHandler(this.container);

        console.log('[HarmoniaMosaic] Carousel mode initialized');
        return;
      }

      // Skip animations if reduced motion
      if (this.prefersReducedMotion) {
        this.showAllItems();
        console.log('[HarmoniaMosaic] Reduced motion detected, animations disabled');
        return;
      }

      // Apply pattern-specific setup
      this.setupPattern();

      // Set item indices for stagger delays
      this.setItemIndices();

      // Setup intersection observer
      this.setupIntersectionObserver();

      // Add Ken Burns class if enabled
      if (this.kenburns) {
        this.container.classList.add('harmonia-mosaic--kenburns');
      }

      // Initialize touch handler for hybrid mode
      this.touchHandler = new MosaicTouchHandler(this.container);

      console.log('[HarmoniaMosaic] Initialized', {
        pattern: this.pattern,
        animation: this.animation,
        displayMode: this.displayMode,
        items: this.items.length,
        kenburns: this.kenburns
      });
    }

    // --------------------------------------------------------
    // Pattern Setup
    // --------------------------------------------------------

    setupPattern() {
      // Scattered pattern: Apply random rotations
      if (this.pattern === 'scattered') {
        this.items.forEach(item => {
          const rotation = (Math.random() * SCATTER_ROTATION_RANGE * 2) - SCATTER_ROTATION_RANGE;
          item.style.setProperty('--scatter-rotate', `${rotation.toFixed(2)}deg`);
        });
      }
    }

    // --------------------------------------------------------
    // Item Index Assignment
    // --------------------------------------------------------

    setItemIndices() {
      if (this.animation === 'random') {
        // Random delays
        this.items.forEach(item => {
          const delay = Math.floor(Math.random() * RANDOM_DELAY_MAX);
          item.style.setProperty('--stagger-delay', `${delay}ms`);
        });
      } else if (this.animation === 'cascade') {
        // Cascade: diagonal ordering
        const columns = this.getColumnCount();
        this.items.forEach((item, index) => {
          const row = Math.floor(index / columns);
          const col = index % columns;
          const diagonalIndex = row + col;
          item.style.setProperty('--item-index', diagonalIndex);
        });
      } else {
        // Default: sequential ordering
        this.items.forEach((item, index) => {
          item.style.setProperty('--item-index', index);
        });
      }
    }

    getColumnCount() {
      const style = getComputedStyle(this.container);
      const columns = style.getPropertyValue('--mosaic-columns');
      return parseInt(columns) || 3;
    }

    // --------------------------------------------------------
    // IntersectionObserver: Scroll-triggered Animation
    // --------------------------------------------------------

    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px 0px -10% 0px', // Trigger slightly before fully in view
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isAnimated) {
            this.triggerAnimation();
          }
        });
      }, options);

      this.observer.observe(this.container);
    }

    // --------------------------------------------------------
    // Animation Trigger
    // --------------------------------------------------------

    triggerAnimation() {
      this.isAnimated = true;

      // Add animation modifier class
      this.container.classList.add('harmonia-mosaic--animate');

      // Add specific animation type class
      if (this.animation !== 'stagger-scale') {
        this.container.classList.add(`harmonia-mosaic--${this.animation}`);
      }

      console.log('[HarmoniaMosaic] Animation triggered');
    }

    showAllItems() {
      // Immediately show all items (for reduced motion)
      this.items.forEach(item => {
        item.style.opacity = '1';
        item.style.transform = 'none';
      });
      this.isAnimated = true;
    }

    // --------------------------------------------------------
    // Public Methods
    // --------------------------------------------------------

    reset() {
      // Destroy existing carousel handler
      if (this.carouselHandler) {
        this.carouselHandler.destroy();
        this.carouselHandler = null;
      }

      // Re-read pattern from data attribute (may have changed in theme editor)
      this.pattern = this.container.dataset.pattern || 'equal-3x3';
      this.displayMode = this.container.dataset.displayMode || 'product';

      // Carousel mode: reinitialize carousel handler
      if (this.pattern === 'carousel' || this.pattern === 'magazine-carousel') {
        this.carouselHandler = new HarmoniaMosaicCarousel(this.container);
        if (this.touchHandler) {
          this.touchHandler.destroy();
        }
        this.touchHandler = new MosaicTouchHandler(this.container);
        return;
      }

      // Reset animation state (useful for theme editor)
      this.isAnimated = false;
      this.container.classList.remove('harmonia-mosaic--animate');
      this.container.classList.remove(`harmonia-mosaic--${this.animation}`);

      this.items = [...this.container.querySelectorAll('.harmonia-mosaic__item')];
      this.items.forEach(item => {
        item.style.opacity = '';
        item.style.transform = '';
        item.style.animation = '';
      });

      // Destroy and re-create touch handler
      if (this.touchHandler) {
        this.touchHandler.destroy();
      }
      this.touchHandler = new MosaicTouchHandler(this.container);

      // Re-setup
      this.setupPattern();
      this.setItemIndices();
    }

    // --------------------------------------------------------
    // Cleanup
    // --------------------------------------------------------

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }

      // Destroy carousel handler
      if (this.carouselHandler) {
        this.carouselHandler.destroy();
        this.carouselHandler = null;
      }

      // Destroy touch handler
      if (this.touchHandler) {
        this.touchHandler.destroy();
      }

      console.log('[HarmoniaMosaic] Destroyed');
    }
  }

  // ============================================================
  // Initialization
  // ============================================================

  function initMosaics() {
    const containers = document.querySelectorAll('.harmonia-mosaic');

    containers.forEach(container => {
      // Skip if already initialized
      if (container.dataset.initialized === 'true') return;

      const mosaic = new HarmoniaMosaic(container);
      container.dataset.initialized = 'true';

      // Store instance for potential cleanup
      container._harmoniaMosaic = mosaic;
    });
  }

  // DOM Ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMosaics);
  } else {
    initMosaics();
  }

  // Shopify Theme Editor Support
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('.harmonia-mosaic');
    if (section) {
      section.dataset.initialized = 'false';
      initMosaics();
    }
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const section = e.target.querySelector('.harmonia-mosaic');
    if (section && section._harmoniaMosaic) {
      section._harmoniaMosaic.destroy();
    }
  });

  // Re-trigger animation when section settings change
  document.addEventListener('shopify:section:reorder', initMosaics);

  // Expose for debugging
  window.HarmoniaMosaic = HarmoniaMosaic;

})();
